import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import { billingApi, BillSummary, BillType, rupees } from '../billing.api';
import BillDetailDialog from '../components/BillDetailDialog';
import { BillStatusChip } from '../components/BillStatusChip';
import ConvertProformasDialog from '../components/ConvertProformasDialog';
import NewBillDialog from '../components/NewBillDialog';
import useBillingFilters from '../hooks/useBillingFilters';

/**
 * Proforma bills and bills (V26). Each is raised from an approved quotation's milestones;
 * a row opens the bill — its PDF, receipts, discount, cancellation, or conversion of a
 * proforma into a bill. Open proformas of one job can be ticked and converted together into
 * one bill, with the amounts adjusted on the way.
 */
export default function BillsPage() {
  const f = useBillingFilters();
  const [type, setType] = useState<BillType | ''>('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<BillSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState<BillType | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [converting, setConverting] = useState<number[]>([]);

  const load = useCallback(async () => {
    if (!f.ready) return;
    setLoading(true);
    try {
      const list = await billingApi.list({ type, financialYearId: f.financialYearId, companyId: f.companyId });
      setRows(list);
      // Keep only ticks on proformas that are still open and still listed.
      setSelected((sel) => sel.filter((id) => list.some((r) => r.id === id && r.billType === 'PROFORMA' && r.status === 'ACTIVE')));
    } catch (e) {
      toast.error(logError({ message: 'Could not load the bills', error: e, code: 'BILLS_LOAD_FAILED' }));
    } finally {
      setLoading(false);
    }
  }, [f.ready, type, f.financialYearId, f.companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.billNo, r.clientName, r.jobCode, r.jobName, r.companyName].some((v) => (v ?? '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  // One bill covers one job's proformas, so once one is ticked only that job's open proformas can be.
  const selectedJob = rows.find((r) => r.id === selected[0])?.jobId ?? null;
  const convertible = (r: BillSummary) => r.billType === 'PROFORMA' && r.status === 'ACTIVE';
  const toggle = (id: number) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const selectedTotal = rows.filter((r) => selected.includes(r.id)).reduce((t, r) => t + Number(r.totalAmount), 0);

  const totals = shown
    .filter((r) => r.billType === 'INVOICE' && r.status === 'ACTIVE')
    .reduce((t, r) => ({ billed: t.billed + Number(r.totalAmount), due: t.due + Number(r.due) }), { billed: 0, due: 0 });

  return (
    <Stack gap={3}>
      <SectionTitle
        title='BILLS'
        action={
          <Stack direction='row' gap={1}>
            <Button variant='outlined' startIcon={<Icon icon='mdi:file-document-outline' />} onClick={() => setCreating('PROFORMA')}>
              New proforma invoice
            </Button>
            <Button variant='contained' startIcon={<Icon icon='mingcute:bill-2-line' />} onClick={() => setCreating('INVOICE')}>
              Tax invoice
            </Button>
          </Stack>
        }
      />

      <Card sx={{ py: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
          <TextField select size='small' label='Financial year' value={f.financialYearId} sx={{ minWidth: 160 }}
            onChange={(e) => f.setFinancialYearId(e.target.value === '' ? '' : Number(e.target.value))}>
            <MenuItem value=''>All years</MenuItem>
            {f.years.map((y) => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
          </TextField>
          <TextField select size='small' label='Company' value={f.companyId} sx={{ minWidth: 220 }}
            onChange={(e) => f.setCompanyId(e.target.value === '' ? '' : Number(e.target.value))}>
            <MenuItem value=''>All companies</MenuItem>
            {f.companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
          </TextField>
          <TextField select size='small' label='Type' value={type} sx={{ minWidth: 150 }}
            onChange={(e) => setType(e.target.value as BillType | '')}>
            <MenuItem value=''>Bills & proformas</MenuItem>
            <MenuItem value='INVOICE'>Bills</MenuItem>
            <MenuItem value='PROFORMA'>Proformas</MenuItem>
          </TextField>
          <TextField size='small' label='Search' placeholder='Bill no., client, job…' value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }} />
        </Stack>
        {selected.length > 0 && (
          <Alert
            severity='info'
            icon={<Icon icon='mdi:file-document-multiple-outline' />}
            sx={{ mb: 2, alignItems: 'center' }}
            action={
              <Stack direction='row' gap={1}>
                <Button color='inherit' size='small' onClick={() => setSelected([])}>
                  Clear
                </Button>
                <Button variant='contained' size='small' onClick={() => setConverting(selected)}>
                  Convert to tax invoice…
                </Button>
              </Stack>
            }
          >
            {selected.length} proforma{selected.length === 1 ? '' : 's'} selected · {rupees(selectedTotal)}. They become one tax
            invoice; you can adjust the amounts before it is raised.
          </Alert>
        )}
        {loading && <LinearProgress />}
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' />
              <TableCell>Bill no.</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Job</TableCell>
              <TableCell>Company</TableCell>
              <TableCell align='right'>Amount</TableCell>
              <TableCell align='right'>GST</TableCell>
              <TableCell align='right'>Total</TableCell>
              <TableCell align='right'>Due</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.id} hover selected={selected.includes(r.id)}
                sx={{ cursor: 'pointer', opacity: r.status === 'CANCELLED' ? 0.55 : 1 }} onClick={() => setOpenId(r.id)}>
                <TableCell padding='checkbox'>
                  {convertible(r) && (
                    <Tooltip
                      title={selectedJob != null && r.jobId !== selectedJob
                        ? 'Only proformas of the same job go on one bill'
                        : 'Tick to convert with other proformas of this job'}
                    >
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox size='small' checked={selected.includes(r.id)}
                          disabled={selectedJob != null && r.jobId !== selectedJob}
                          onChange={() => toggle(r.id)} inputProps={{ 'aria-label': `Select ${r.billNo}` }} />
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='body2' fontWeight={600}>{r.billNo}</Typography>
                  <Typography variant='caption' color='text.secondary'>{r.billType === 'PROFORMA' ? 'Proforma' : 'Bill'}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.billDate).format('DD MMM YYYY')}</TableCell>
                <TableCell>{r.clientName ?? '—'}</TableCell>
                <TableCell>
                  <Typography variant='body2'>{r.jobCode}</Typography>
                  <Typography variant='caption' color='text.secondary'>{r.jobName}</Typography>
                </TableCell>
                <TableCell>{r.companyName}</TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{rupees(r.amount)}</TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{rupees(r.gstAmount)}</TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}><b>{rupees(r.totalAmount)}</b></TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{r.billType === 'INVOICE' ? rupees(r.due) : '—'}</TableCell>
                <TableCell><BillStatusChip bill={r} /></TableCell>
              </TableRow>
            ))}
            {!loading && shown.length === 0 && (
              <TableRow>
                <TableCell {...({ colSpan: 11 } as object)} align='center' sx={{ py: 4, color: 'text.secondary' }}>
                  No bills for these filters. Raise one from an approved quotation with “New proforma invoice” or “Tax invoice”.
                </TableCell>
              </TableRow>
            )}
            {shown.length > 0 && (
              <TableRow>
                <TableCell {...({ colSpan: 8 } as object)}><b>Live bills shown</b></TableCell>
                <TableCell align='right'><b>{rupees(totals.billed)}</b></TableCell>
                <TableCell align='right'><b>{rupees(totals.due)}</b></TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <NewBillDialog open={creating != null} billType={creating ?? 'INVOICE'} onClose={() => setCreating(null)}
        onCreated={(b) => { setCreating(null); load(); setOpenId(b.bill.id); }} />
      <ConvertProformasDialog proformaIds={converting} onClose={() => setConverting([])}
        onConverted={(d) => { setConverting([]); setSelected([]); load(); setOpenId(d.bill.id); }} />
      <BillDetailDialog billId={openId} onClose={() => setOpenId(null)} onChanged={load} onOpenBill={setOpenId} />
    </Stack>
  );
}
